import React, { useState } from 'react';
import { authService } from '../../../services/authService';
import Icon from '../../../components/AppIcon';

const ForgotPasswordForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(''); // 'smtp' or 'general'
  const [validationError, setValidationError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e?.target?.value;
    setEmail(value);
    setError('');
    setErrorType('');
    
    if (value && !validateEmail(value)) {
      setValidationError('Please enter a valid email address');
    } else {
      setValidationError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setErrorType('');

    if (!email) {
      setValidationError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { error: resetError } = await authService?.sendPasswordResetEmail(email);

      if (resetError) {
        // Detect SMTP configuration issues
        const errorMessage = resetError?.toLowerCase() || '';
        const isSmtpError = errorMessage?.includes('smtp') || 
                           errorMessage?.includes('email') || 
                           errorMessage?.includes('recovery') ||
                           errorMessage?.includes('mail') ||
                           errorMessage?.includes('redirect') ||
                           errorMessage?.includes('url');
        
        if (isSmtpError) {
          setErrorType('smtp');
          setError('Email configuration issue detected. Please ensure: (1) Custom SMTP is enabled in Supabase, (2) Resend API key is correct, (3) Sender domain is verified, and (4) Redirect URLs are whitelisted.');
        } else {
          setErrorType('general');
          setError(resetError);
        }
        setLoading(false);
        return;
      }

      // Success - notify parent component
      onSuccess(email);
    } catch (err) {
      const errorMessage = err?.message?.toLowerCase() || '';
      const isSmtpError = errorMessage?.includes('smtp') || 
                         errorMessage?.includes('email') || 
                         errorMessage?.includes('recovery') ||
                         errorMessage?.includes('mail');
      
      if (isSmtpError) {
        setErrorType('smtp');
        setError('Email service is currently unavailable. Please contact support or try again later.');
      } else {
        setErrorType('general');
        setError(err?.message || 'Failed to send reset email. Please try again.');
      }
      setLoading(false);
    }
  };

  const isFormValid = email && validateEmail(email) && !validationError;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className={`p-4 border rounded-lg flex items-start gap-3 ${
          errorType === 'smtp' ?'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800' :'bg-destructive/10 border-destructive/20'
        }`}>
          <Icon 
            name={errorType === 'smtp' ? 'AlertTriangle' : 'AlertCircle'} 
            size={20} 
            color={errorType === 'smtp' ? '#f59e0b' : 'var(--color-destructive)'} 
            className="flex-shrink-0 mt-0.5" 
          />
          <div className="flex-1">
            <p className={`text-sm font-medium mb-1 ${
              errorType === 'smtp' ? 'text-amber-800 dark:text-amber-200' : 'text-destructive'
            }`}>
              {error}
            </p>
            {errorType === 'smtp' && (
              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300 space-y-1">
                <p className="font-medium">What you can do:</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>Contact our support team for assistance</li>
                  <li>Try again in a few minutes</li>
                  <li>Verify your email address is correct</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
          Email Address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Mail" size={20} color="var(--color-muted-foreground)" />
          </div>
          <input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="agent@example.com"
            className={
              `w-full pl-10 pr-4 py-3 bg-background border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 transition-all ${
                validationError 
                  ? 'border-destructive focus:ring-destructive/20' :'border-border focus:ring-primary/20'
              }`
            }
            disabled={loading}
            autoComplete="email"
            autoFocus
          />
        </div>
        {validationError && (
          <p className="mt-2 text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {validationError}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!isFormValid || loading}
        className={
          `w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            !isFormValid || loading
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md'
          }`
        }
      >
        {loading ? (
          <>
            <Icon name="Loader2" size={20} className="animate-spin" />
            Sending Reset Link...
          </>
        ) : (
          <>
            <Icon name="Send" size={20} />
            Send Reset Link
          </>
        )}
      </button>

      <div className="pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          You'll receive an email with a secure link to reset your password. The link expires in 1 hour.
        </p>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;